import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subscription, map } from 'rxjs';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { UserDTO, UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserService } from 'src/app/common/state/user/user.service';
import { UserState } from 'src/app/common/state/user/user.state';
import { APILoadingStatus } from 'src/app/common/types/types';
import { Animations } from 'src/app/common/utilities/animations';
import { select } from 'src/app/common/utilities/ngxs-utils';
import {
  checkIfCoach,
  checkIfCreator,
  checkIfShadower,
  checkIfValidURL,
  checkPresentAttendee,
} from 'src/app/common/utilities/session-helpers';
import { TimezoneLinkBack } from 'src/app/common/utilities/time-helpers';
import { DatepickerOutput } from 'src/app/private/shared/components/datepicker/datepicker.component';
import { getValidTimepickerValue } from 'src/app/private/shared/components/datepicker/datepicker.helpers';
import { FileListComponent } from 'src/app/private/shared/components/file-management/file-list/file-list.component';
import {
  FileUploadComponent,
  UploadStyles,
} from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { SessionTypeModalComponent } from 'src/app/private/shared/components/session-type-modal/session-type-modal.component';
import {
  CoachingSessionAttendeeDTO,
  CoachingSessionDTO,
} from 'src/app/private/shared/dtos/coaching-session.dto';
import { FileDTO } from 'src/app/private/shared/dtos/file.dto';
import { PusherAttendeeDTO } from 'src/app/private/shared/dtos/pusher-user.dto';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { checkSessionDataType } from 'src/app/private/shared/helpers/coachee-log.utilities';
import { fileDTOFromEvidenceAttachment } from 'src/app/private/shared/helpers/translators/evidence.translators';
import { AlertService } from 'src/app/private/shared/services/alert/alert.service';
import { CoachingLogService } from 'src/app/private/shared/services/coaching-log/coaching-log.service';
import { LogFileService } from 'src/app/private/shared/services/file/log.file.service';
import { FEATURE_FLAGS } from 'src/app/private/shared/services/unleash/unleash.helpers';
import { UnleashService } from 'src/app/private/shared/services/unleash/unleash.service';
import { UserSearchService } from 'src/app/private/shared/services/user-search/user-search.service';
import { FetchSessionData } from 'src/app/private/shared/state/coaching-log/coaching-log.actions';
import { CoachingLogState } from 'src/app/private/shared/state/coaching-log/coaching-log.state';
import {
  AbsentReasonsAPIResponse,
  CoachlogTypesAPIResponse,
  ViewLogAPIResponse,
} from 'src/app/private/shared/types/responses/coaching-log.responses';

@Component({
  selector: 'app-observation-info-page',
  templateUrl: './observation-info-page.component.html',
  styleUrls: ['./observation-info-page.component.scss'],
  animations: [Animations.fadeInOut],
  providers: [UserSearchService],
  standalone: false,
})
export class ObservationInfoPageComponent implements OnInit, OnDestroy {
  @ViewChild('sessionTypeModal')
  sessionTypeModal: SessionTypeModalComponent;

  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @ViewChild('fileListComponent') fileListComponent: FileListComponent;

  @Output() readonly uploads = new EventEmitter<FileDTO>();

  sessionId: number;

  sessionData$ = select(CoachingLogState.getSessionData);

  coachLogStatus$: Observable<APILoadingStatus> = select(
    CoachingLogState.getCoachLogStatus
  );

  coachLogStatus: APILoadingStatus;

  sessionLoaded = false;

  user: User;

  sessionData: CoachingSessionDTO | null;

  selectedAttendee: UserDTO | null;

  sessionAttendees: UserDTO[] | null;

  subs: Subscription[] = [];

  selectedLogId: number | null;

  attendeeSelectApiCalls: Subscription | null;

  coachlogTypesList: CoachlogTypesAPIResponse[];

  absentReasonsList: AbsentReasonsAPIResponse[];

  isCoach: boolean;

  isCreator: boolean;

  isShadower: boolean;

  selectedTypeId: number;

  logStart: string;

  logEnd: string;

  timezone: TimezoneLinkBack;

  latestLogId: number;

  isTimeValid = true;

  hasPresentAttendee = false;

  districtId: number | null = null;

  includeAllB2bDistricts = false;

  pusherAttendees: PusherAttendeeDTO[];

  uploadType = UploadType.LOG;

  uploadStyle = UploadStyles.SIMPLE;

  attachments: FileDTO[] = [];

  hideUpload: boolean;

  smartFeedbackActive = false;

  smartFeedbackTabActive = true;

  prevSubmissionsActive = false;

  prevSubmissionsTabActive = true;

  notesActive = false;

  notesTabActive = true;

  commentsActive = false;

  commentsTabActive = true;

  resourcesActive = false;

  resourcesTabActive = true;

  sessionCreator: UserDTO | null;

  urlError = '';

  observationProFormsEnabled = false;

  participantViewMap = new Map<number, ViewLogAPIResponse | null>();

  constructor(
    private route: ActivatedRoute,
    public coachingService: CoachingLogService,
    private router: Router,
    private store: Store,
    private alertService: AlertService,
    private userService: UserService,
    private featureFlagService: UnleashService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.observationProFormsEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.observationProForms
    );
  }

  ngOnInit(): void {
    this.route.params.subscribe((url) => {
      this.sessionId = parseInt(url['logId']);
      this.coachingService.currentPage = 'observation-info';
      this.store.dispatch(new FetchSessionData(this.sessionId));
    });

    // if user is not an e2L employee, filter user select options to only users in their district
    if (this.user) {
      if (
        !this.user.roles.includes(UserRole.E2L_EMPLOYEE) &&
        this.user.district
      ) {
        this.districtId = this.user.district.id;
        if (this.user.district.isB2b === 1) {
          this.includeAllB2bDistricts = true;
        }
      }
    }

    this.sessionData$
      .pipe(
        map((filterFn) =>
          (filterFn as (id: number) => CoachingSessionDTO)(this.sessionId)
        )
      )
      .pipe(
        map((sessionData) => {
          if (sessionData) {
            this.sessionData = sessionData;

            this.userService
              .fetchUserById(this.sessionData.creatorUserId)
              .subscribe((user) => {
                if (user) {
                  this.sessionCreator = user;
                }
              });

            checkSessionDataType(sessionData, 'observation', this.router);

            this.isCoach = checkIfCoach(this.sessionData);
            this.isCreator = checkIfCreator(this.sessionData, this.user.id);
            this.isShadower = checkIfShadower(this.sessionData);
            this.hasPresentAttendee = checkPresentAttendee(this.sessionData);

            if (
              (this.isCoach || this.isCreator) &&
              this.participantViewMap.size === 0
            ) {
              this.loadParticipantViews();
            }

            if (this.sessionData.egroweCoachlogTypeId) {
              this.selectedTypeId = this.sessionData.egroweCoachlogTypeId;
            }

            this.logStart = getValidTimepickerValue(
              this.sessionData.startDatetime,
              this.sessionData.timezone
            );
            this.logEnd = getValidTimepickerValue(
              this.sessionData.endDatetime,
              this.sessionData.timezone
            );
            this.timezone = this.sessionData.timezone as TimezoneLinkBack;
            if (!this.selectedAttendee && this.sessionData?.attendees?.length) {
              if (!this.sessionAttendees) {
                this.sessionAttendees = [];
              }
              this.sessionAttendees = this.sessionData.attendees.map(
                (attendee) => attendee.user
              );

              if (this.sessionAttendees?.length) {
                const first = this.sessionAttendees[0];
                this.selectedAttendee = first;
              }
            }

            if (this.sessionData.attachments) {
              this.attachments = this.sessionData.attachments?.map(
                (attachment) => fileDTOFromEvidenceAttachment(attachment)
              );
              this.hideUpload = this.attachments.length > 0;
            }

            setTimeout(() => {
              if (this.sessionId && this.uploadComponent) {
                (this.uploadComponent.fileService as LogFileService).setLogId(
                  this.sessionId
                );
              }
              this.fileListComponent.fileListUpdate();
            }, 500);

            this.sessionLoaded = true;
          }
        })
      )
      .subscribe();

    this.coachLogStatus$.subscribe((status) => {
      if (status.loaded) {
        this.coachLogStatus = status;
        this.sessionLoaded = true;
      }
    });

    this.subs.push(
      this.coachingService.getCoachlogTypes().subscribe((response) => {
        this.coachlogTypesList = response.items;
        // temp to restrict log types
        this.coachlogTypesList = this.coachlogTypesList.filter(
          (logType) => logType.title === 'Coaching Conversation'
        );
      })
    );

    this.subs.push(
      this.coachingService.getAbsentReasons().subscribe((response) => {
        this.absentReasonsList = response.items;
      })
    );

    this.subs.push(
      this.coachingService.attendancePusherService.attendeeRostersNotifier.subscribe(
        () => {
          this.getAttendees();
        }
      )
    );

    this.subs.push(
      this.coachingService.logPusherService.attendeeRostersNotifier.subscribe(
        () => {
          this.getAttendees();
        }
      )
    );
  }

  updateAttachments() {
    this.hideUpload = true;
    this.fileListComponent.fileListUpdate();
  }

  getAttendees() {
    const attendeesOnPage =
      this.coachingService.attendancePusherService.attendeeRosters[
        `${this.sessionId}-observation-info`
      ];

    if (attendeesOnPage && attendeesOnPage.length > 0) {
      this.pusherAttendees =
        this.coachingService.logPusherService.attendeeRosters[
          this.sessionId
        ].map((sessionAttendee) => {
          const samePage = attendeesOnPage.some(
            (pageAttendee) => pageAttendee.id === sessionAttendee.id
          );
          return {
            avatar: sessionAttendee.avatar,
            samePage,
            id: sessionAttendee.id,
          };
        });
    }
  }

  getPreviousLogId(
    attendeeId: number,
    sessionDate: number,
    logType: number
  ): Observable<number | null> {
    sessionDate -= 1;
    return this.coachingService
      .getPreviousLogs(attendeeId, sessionDate, logType)
      .pipe(
        map((logs) => {
          if (logs?.length) {
            this.latestLogId = logs[0].id;
            return this.latestLogId;
          }
          return null;
        })
      );
  }

  addCoachee(newCoachee: UserLiteDTO | null) {
    if (newCoachee) {
      // check if shadower...
      const isShadower = this.sessionData?.shadowers.find(
        (item) => item.userId === newCoachee.id
      );
      const isCreator = this.sessionData?.creatorUserId === newCoachee.id;
      if (!isShadower && !isCreator) {
        this.coachingService.addAttendee(newCoachee.id, this.sessionData?.id);
      } else if (isShadower) {
        this.alertService.showAlert(
          'Selected User is already a shadower of this session'
        );
      } else if (isCreator) {
        this.alertService.showAlert(
          'The creator of the session cannot be a participant'
        );
      }
    }
  }

  removeCoachee(coachee: CoachingSessionAttendeeDTO) {
    if (this.sessionData) {
      this.sessionData.attendees = this.sessionData.attendees.filter(
        (attendee) => attendee.id !== coachee.id
      );
    }
  }

  addShadower(newShadower: UserLiteDTO | null) {
    if (newShadower) {
      const isCoachee = this.sessionData?.attendees.find(
        (item) => item.userId === newShadower.id
      );
      const isCreator = this.sessionData?.creatorUserId === newShadower.id;
      if (!isCoachee && !isCreator) {
        this.coachingService.addShadower(newShadower.id, this.sessionData?.id);
      } else if (isCoachee) {
        this.alertService.showAlert(
          'Selected User is already a participant of this session'
        );
      } else if (isCreator) {
        this.alertService.showAlert(
          'The creator of the session cannot be a shadower'
        );
      }
    }
  }

  saveTitle() {
    if (this.sessionData) {
      const coachlogUpdates = { title: this.sessionData?.title };
      this.coachingService.updateCoachingLog(
        this.sessionData?.id,
        coachlogUpdates
      );
    }
  }

  saveVirtualMeeting() {
    if (this.sessionData) {
      if (checkIfValidURL(this.sessionData?.virtualMeeting)) {
        const coachlogUpdates = {
          virtual_meeting: this.sessionData?.virtualMeeting,
        };
        this.coachingService.updateCoachingLog(
          this.sessionData?.id,
          coachlogUpdates
        );
        this.urlError = '';
      } else {
        this.urlError = 'Must be a valid URL';
      }
    }
  }

  updateDateTime(timeUpdate: DatepickerOutput[]) {
    const startJsTimestamp = timeUpdate[0].time / 1000;
    const endJsTimestamp = timeUpdate[1].time / 1000;
    this.logStart = getValidTimepickerValue(
      startJsTimestamp,
      timeUpdate[0].timezone
    );
    this.timezone = timeUpdate[0].timezone;
    this.logEnd = getValidTimepickerValue(
      endJsTimestamp,
      timeUpdate[0].timezone
    );
    if (this.isTimeValid) {
      this.coachingService.updateCoachingLog(this.sessionData?.id as number, {
        start_datetime: startJsTimestamp,
        end_datetime: endJsTimestamp,
        timezone: this.timezone,
      });
    }
  }

  navigateNextScreen() {
    if (this.hasPresentAttendee) {
      this.router.navigate([`/observation/log/${this.sessionData?.id}/form`]);
    }
  }

  onIsPrivateChange($event: Event): void {
    const target = $event.target as HTMLInputElement | null;
    const coachlogUpdates = {
      is_private: false,
    };

    if (target?.checked) {
      coachlogUpdates.is_private = true;
    }

    if (this.sessionData) {
      this.coachingService.updateCoachingLog(
        this.sessionData?.id,
        coachlogUpdates
      );
    }
  }

  onSyncToCalendarChange($event: Event): void {
    const target = $event.target as HTMLInputElement | null;
    const coachlogUpdates = {
      sync_to_calendar: target?.checked,
    };

    if (this.sessionData) {
      this.coachingService.updateCoachingLog(
        this.sessionData?.id,
        coachlogUpdates
      );
    }
  }

  deleteSession() {
    this.coachingService.deleteCurrentSession(this.sessionId).subscribe();
  }

  openSessionTypeModal() {
    this.sessionTypeModal.openModal();
  }

  toggleSmartFeedback(): void {
    this.smartFeedbackActive = !this.smartFeedbackActive;
    this.smartFeedbackTabActive = !this.smartFeedbackTabActive;
  }

  togglePreviousSubmissions(): void {
    this.prevSubmissionsActive = !this.prevSubmissionsActive;
    this.prevSubmissionsTabActive = !this.prevSubmissionsTabActive;
  }

  toggleNotes(): void {
    this.notesActive = !this.notesActive;
    this.notesTabActive = !this.notesTabActive;
  }

  toggleComments(): void {
    this.commentsActive = !this.commentsActive;
    this.commentsTabActive = !this.commentsTabActive;
  }

  toggleResources(): void {
    this.resourcesActive = !this.resourcesActive;
    this.resourcesTabActive = !this.resourcesTabActive;
  }

  loadParticipantViews() {
    if (this.sessionData?.attendees) {
      this.sessionData.attendees.forEach((attendee) => {
        this.coachingService
          .checkObserveeViewed(this.sessionId, attendee.userId)
          .subscribe((resp) => {
            this.participantViewMap.set(attendee.userId, resp);
          });
      });
    }
  }

  isViewStatusLoaded(userId: number): boolean {
    return this.participantViewMap.has(userId);
  }

  getParticipantView(userId: number): ViewLogAPIResponse | null {
    return this.participantViewMap.get(userId) ?? null;
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }

    this.coachingService.disconnectFromPusherChannel(this.sessionId);
  }
}
